import type { ResourceLanguage } from 'i18next';

type FlatDictionary = Record<string, string>;

interface TranslationApiResponse {
  responseData?: {
    translatedText?: string;
  };
}

const DEFAULT_TRANSLATION_ENDPOINT = 'https://api.mymemory.translated.net/get';

const flattenDictionary = (
  input: ResourceLanguage,
  prefix = '',
  result: FlatDictionary = {},
): FlatDictionary => {
  Object.entries(input).forEach(([key, value]) => {
    const nextKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      result[nextKey] = value;
      return;
    }

    if (value && typeof value === 'object') {
      flattenDictionary(value as ResourceLanguage, nextKey, result);
    }
  });

  return result;
};

const unflattenDictionary = (flatDictionary: FlatDictionary): ResourceLanguage => {
  const result: Record<string, unknown> = {};

  Object.entries(flatDictionary).forEach(([flatKey, value]) => {
    const segments = flatKey.split('.');
    let cursor: Record<string, unknown> = result;

    segments.forEach((segment, index) => {
      const isLastSegment = index === segments.length - 1;

      if (isLastSegment) {
        cursor[segment] = value;
        return;
      }

      if (!cursor[segment] || typeof cursor[segment] !== 'object') {
        cursor[segment] = {};
      }

      cursor = cursor[segment] as Record<string, unknown>;
    });
  });

  return result as ResourceLanguage;
};

export const translateText = async (
  text: string,
  targetLanguage: string,
  sourceLanguage = 'en',
): Promise<string> => {
  if (!text.trim()) {
    return text;
  }

  const endpoint = import.meta.env.VITE_TRANSLATION_API_URL ?? DEFAULT_TRANSLATION_ENDPOINT;
  const email = import.meta.env.VITE_TRANSLATION_API_EMAIL as string | undefined;
  const apiKey = import.meta.env.VITE_TRANSLATION_API_KEY as string | undefined;

  const params = new URLSearchParams({
    q: text,
    langpair: `${sourceLanguage}|${targetLanguage}`,
  });

  if (email) {
    params.set('de', email);
  }

  if (apiKey) {
    params.set('key', apiKey);
  }

  try {
    const response = await fetch(`${endpoint}?${params.toString()}`);
    if (!response.ok) {
      return text;
    }

    const data = (await response.json()) as TranslationApiResponse;
    return data.responseData?.translatedText || text;
  } catch {
    return text;
  }
};

export const translateResourceBundle = async (
  sourceBundle: ResourceLanguage,
  targetLanguage: string,
): Promise<ResourceLanguage> => {
  const flatSource = flattenDictionary(sourceBundle);
  const translatedEntries = await Promise.all(
    Object.entries(flatSource).map(async ([key, value]) => {
      const translatedValue = await translateText(value, targetLanguage);
      return [key, translatedValue] as const;
    }),
  );

  return unflattenDictionary(Object.fromEntries(translatedEntries));
};
